import {Component, NgZone, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {map, take, tap} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {Route} from 'src/app/enum/route';
import {Store} from '@ngrx/store';
import {ProposalAppState} from '../../store/proposal/reducers/proposal.reducer';
import {selectDetailPageOffers, selectDetailPageProposalTile,} from '../../store/proposal/selectors/proposal.selectors';
import {DialogTwoOptionsComponent} from '../../components/dialogs/two-options-dialog/dialog-two-options.component';
import {LocalStorageItem} from '../../enum/local-storage-item';
import {FilterRole} from '../../enum/filter-role';
import {TranslateService} from '@ngx-translate/core';
import {SpinnerDialogComponent} from '../../components/dialogs/spinner-dialog/spinner-dialog.component';
import {UploadComponent} from '../../components/upload-field/upload.component';
import {ProposalDetailPageActions} from '../../store/proposal/actions/proposal.actions';
import {animate, style, transition, trigger} from '@angular/animations';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {Offer} from '../../model/offer/offer';
import {ProposalUser} from '../../model/proposal/proposalUser';
import {DetailsPageTab} from '../../enum/details-page-tabs';
import {OfferService} from '../../services/offer/offer.service';
import {
  OfferFormBase
} from '../../components/proposal-details-components/create-offer-tab/create-offer-elements/offer-form-base/offer-form-base.component';
import {
  OfferFileUploadComponent
} from '../../components/proposal-details-components/create-offer-tab/create-offer-elements/offer-file-upload/offer-file-upload.component';
import {selectUser} from '../../store/user/selectors/user.selectors';
import {UserAppState} from '../../store/user/reducers/user.reducer';
import {QuestionOptions} from '../../constants/question-options';
import {OfferType} from '../../enum/offer-type';
import {OfferStatus} from '../../enum/offer-status';
import {MatAccordion} from '@angular/material/expansion';
import {OfferTransferService} from '../../services/offer/offer-transfer.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FileUploadService} from '../../services/file-upload-service/file-upload.service';

@Component({
    selector: 'app-offer-preview-page',
    templateUrl: './offer-preview-page.component.html',
    styleUrls: ['./offer-preview-page.component.scss', '../proposal-detail/proposal-details.component.scss', '../../../styles/create-something.scss', '../../components/proposal-details-components/offers-list-tab/offers-details-tab/offers-details-tab.component.scss'],
    animations: [
        trigger('swipeAnimation', [
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('400ms ease-in-out', style({ transform: 'translateX(0%)' })),
            ]),
            transition(':leave', [
                animate('400ms ease-in-out', style({ transform: 'translateX(100%)' })),
            ]),
        ])
    ],
    standalone: false
})
export class OfferPreviewPageComponent implements OnInit {

  offer = new Offer();

  Route = Route;
  showComponent: boolean = true;

  allExpandState = false;

  user$ = this.userStore.select(selectUser);

  basedOnOfferId: string | null = null;

  @ViewChildren(UploadComponent) uploadComponents: QueryList<UploadComponent>;
  @ViewChild(MatAccordion) accordion: MatAccordion;

  @ViewChildren(OfferFormBase) formComponents: QueryList<OfferFormBase>;
  @ViewChild(OfferFileUploadComponent) offerFileUploadComponent: OfferFileUploadComponent;
  proposalTile$ = this.proposalStore.select(selectDetailPageProposalTile);

  insuredAmount: number;

  constructor(readonly activatedRoute: ActivatedRoute,
              private readonly translate: TranslateService,
              readonly dialog: MatDialog,
              readonly router: Router,
              private readonly snackbar: MatSnackBar,
              public ngZone: NgZone,
              public proposalStore: Store<ProposalAppState>,
              public offerService: OfferService,
              private readonly userStore: Store<UserAppState>,
              private readonly offerTransferService: OfferTransferService,
              private readonly fileUploadService: FileUploadService) {}


  ngOnInit(): void {
    this.proposalTile$.pipe(take(1)).subscribe((tile) => {
      this.insuredAmount = tile.insuredAmount;
    });
    const offer = this.offerTransferService.offer;
    const basedOnOfferId = this.offerTransferService.basedOnOfferId;

    this.offerTransferService.clear();

    if (offer) {
      this.offer = new Offer(offer);
      this.basedOnOfferId = basedOnOfferId;
    } else {
      console.warn('No offer passed to preview page.');
    }
  }


  openConfirmationMatDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      title: this.translate.instant( this.offer.offerType == OfferType.EXTERNAL_COVERAGE ? 'createOffer.external.confirmation.text' : 'proposalDetails.createOfferTab.confirmationDialog.title'),
      optionCancel: this.translate.instant('proposalDetails.createOfferTab.confirmationDialog.no'),
      optionConfirm: this.translate.instant('proposalDetails.createOfferTab.confirmationDialog.yes'),
    };
    const dialogRef = this.dialog.open(DialogTwoOptionsComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        localStorage.setItem(LocalStorageItem.SELECTED_ROLE, FilterRole.ALL);
        this.openSpinnerDialog(this.basedOnOfferId);
      }

    });
  }

  openSpinnerDialog(offerId?: string) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      title: this.translate.instant('proposalDetails.createOfferTab.confirmationDialog.title'),
    };
    const dialogRef = this.dialog.open(SpinnerDialogComponent, dialogConfig);
    if (offerId) {
      this.proposalStore.select(selectDetailPageOffers).pipe(
        map(offers => offers.find(o => o.offerId === offerId)),
        take(1)
      ).subscribe(originalOffer => {
        let newOffer = new Offer(originalOffer);

        newOffer.standardTermsAndConditions = this.offer.standardTermsAndConditions;
        newOffer.standardClauses = this.offer.standardClauses;
        newOffer.amountCovered = this.offer.amountCovered;
        newOffer.premium = this.offer.premium;
        newOffer.expectedTotalPremium = this.offer.expectedTotalPremium;
        newOffer.advancePremium = this.offer.advancePremium;
        newOffer.minimumPremium = this.offer.minimumPremium;
        newOffer.currency = this.offer.currency;
        newOffer.individualTermsAndConditions = this.offer.individualTermsAndConditions;
        newOffer.individualClauses = this.offer.individualClauses;
        newOffer.attachedFiles = this.offer.attachedFiles;
        newOffer.deductibleAbsoluteAmount = this.offer.deductibleAbsoluteAmount;
        newOffer.deductibleCurrency = this.offer.deductibleCurrency;
        newOffer.typeOfDeductible = this.offer.typeOfDeductible;
        newOffer.rejectionText = this.offer.rejectionText;
        newOffer.otherInformation = this.offer.otherInformation;
        newOffer.offerDescription = this.offer.offerDescription;
        newOffer.referenceValue = this.offer.referenceValue;
        newOffer.premiumInPercent = this.offer.premiumInPercent;
        newOffer.premiumPerThousand = this.offer.premiumPerThousand;

        //Both "new based on" and "edit" are considered as an editing of the offer
        newOffer.lastTimeEdited = new Date();
        newOffer.status = "OPEN";

        this.refillOffer(dialogRef, newOffer, originalOffer);
      })
    } else {
      this.saveOffer(dialogRef);
    }
  }

  sendOffer(dialogRef: MatDialogRef<SpinnerDialogComponent>) {

    this.offerService.saveOffer(this.offer).pipe(tap({
      next: () => this.snackbar.open(this.translate.instant(this.offer.offerType === OfferType.EXTERNAL_COVERAGE ? 'offer.externalCoverage.created' : 'offer.createOffer.created'), '', {
        panelClass: ['success-snackbar', 'custom-snackbar'],
      })
    }),).subscribe(() => {
      this.proposalStore.dispatch(ProposalDetailPageActions.load({proposalId: this.offer.proposalId}));
      this.router.navigate(['/' + Route.PROPOSAL_DETAILS, this.offer.proposalId], {queryParams: {tab: DetailsPageTab.OFFERS}}).then(() => {
        dialogRef.close();
      });
    });
  }

  clickButton($event: any): void {
    if ($event) {
      this.accordion.openAll();
    } else {
      this.accordion.closeAll();
    }
  }

  saveOffer(dialogRef: MatDialogRef<SpinnerDialogComponent>) {

    this.user$.pipe(take(1)).subscribe(user => this.offer.creator = new ProposalUser({id: user.id}));

    this.sendOffer(dialogRef);
  }

  private refillOffer(dialogRef: MatDialogRef<SpinnerDialogComponent, any>, newOffer: Offer, originalOffer: Offer) {
    // if there are some files in the original offer that aren't anymore in the new edited offer, that means that those files got deleted
    // this for loop and endpoint call ensures that the file is deleted on backend as well and not just on the UI
    originalOffer.attachedFiles.forEach(file => {
      if (!newOffer.attachedFiles.some(f => f.id === file.id)) {
        this.fileUploadService.deleteFileData(file.id);
      }
    })

    this.offerService.refillOffer(newOffer).pipe(tap({
      next: () => this.snackbar.open(this.translate.instant(this.offer.offerType === OfferType.EXTERNAL_COVERAGE ? 'offer.externalCoverage.created' : 'offer.createOffer.created'), '', {
        panelClass: ['success-snackbar', 'custom-snackbar'],
      })
    }),).subscribe(() => {
      this.proposalStore.dispatch(ProposalDetailPageActions.load({proposalId: this.offer.proposalId}));
      this.router.navigate(['/' + Route.PROPOSAL_DETAILS, newOffer.proposalId], {queryParams: {tab: DetailsPageTab.OFFERS}}).then(() => {
        dialogRef.close();
      });
    });
  }

  navigateToCreateOffer(): void {
    if(this.offer.offerType === OfferType.EXTERNAL_COVERAGE){
      this.ngZone.run(() => {
        this.router.navigate(['/' + Route.CREATE_EXTERNAL_COVERAGE, this.offer.proposalId]).then();
      });
    } else {
        this.ngZone.run(() => {
          this.router.navigate(['/' + Route.CREATE_OFFER, this.offer.proposalId]).then();
        });
    }
  }

  cancel() {
    this.router.navigate(['/' + Route.INSERATE]).then();
  }

  back() {

    this.offerTransferService.offer = this.offer;

    if(this.offer.offerType === OfferType.EXTERNAL_COVERAGE){
      this.ngZone.run(() => {
        this.router.navigate(['/' + Route.CREATE_EXTERNAL_COVERAGE, this.offer.proposalId]).then();
      });
    } else {
      this.ngZone.run(() => {
        this.router.navigate(['/' + Route.CREATE_OFFER, this.offer.proposalId],
          {queryParams: {basedOnOffer: this.basedOnOfferId}}
        ).then();
      });
    }
  }


  protected readonly QuestionOptions = QuestionOptions;
  protected readonly OfferType = OfferType;
  protected readonly OfferStatus = OfferStatus;
}

